import { useCallback, useEffect } from 'react';
import { captureException } from '@sentry/react';
import axios from 'axios';

import { authSessionService, withAccountSessionLock } from '@/services/auth/sessions';
import { isApiServiceError } from '@/services/http/errors';
import { useAuthStore } from '@/stores/auth';
import { setAccountTransition, useMultiAccountStore } from '@/stores/auth/multiAccount';

const CHANNEL = 'countercyclical-account-session';
let channel: BroadcastChannel | undefined;
const broadcast = (type: 'changing' | 'changed' | 'failed') => {
    if (typeof BroadcastChannel !== 'undefined') {
        channel ??= new BroadcastChannel(CHANNEL);
        channel.postMessage({ type });
    }
};
const reloadContext = (path = '/') => window.location.assign(path);

function accountActionError(error: unknown) {
    const data: unknown = isApiServiceError(error)
        ? error.data
        : axios.isAxiosError(error)
          ? error.response?.data
          : undefined;
    const code = data && typeof data === 'object' && 'code' in data ? data.code : undefined;
    if (code === 'ACCOUNT_LIMIT_REACHED') {
        return 'Sign out of an account before adding another.';
    }
    if (code === 'ACCOUNT_SESSION_EXPIRED') {
        return 'This account session has expired. Sign in again to continue.';
    }
    if (code === 'WORKSPACE_MEMBERSHIP_INACTIVE') {
        return 'You no longer have access to this workspace.';
    }
    if (code === 'AUTHENTICATION_METHOD_DENIED') {
        return 'This workspace requires a different sign-in method.';
    }
    if (code === 'SESSION_CONTEXT_CHANGED') {
        return 'Your account changed in another tab. Refresh to continue.';
    }
    return 'Could not update your account session. Please try again.';
}

export async function loadAccountSessions(registerCurrent = false) {
    useMultiAccountStore.setState({ loading: true, error: undefined });
    try {
        const result = await withAccountSessionLock(async () => {
            if (registerCurrent) {
                await authSessionService.registerAccount();
            }
            return authSessionService.listAccounts();
        });
        useMultiAccountStore.setState({
            accounts: result.accounts,
            maxAccounts: result.maxAccounts,
            mostRecentWorkspace: result.mostRecentWorkspace ?? null,
        });
    } catch (error) {
        captureException(error, { tags: { operation: 'account-session.list' } });
        useMultiAccountStore.setState({ error: accountActionError(error) });
    } finally {
        useMultiAccountStore.setState({ loading: false });
    }
}

export function useAccountSessionSync() {
    useEffect(() => {
        if (typeof BroadcastChannel === 'undefined') {
            return;
        }
        const listener = new BroadcastChannel(CHANNEL);
        let recoveryTimer: ReturnType<typeof setTimeout> | undefined;
        listener.onmessage = (event: MessageEvent<unknown>) => {
            if (!event.data || typeof event.data !== 'object' || !('type' in event.data)) {
                return;
            }
            if (event.data.type === 'changing') {
                setAccountTransition(true);
                clearTimeout(recoveryTimer);
                // Recover if the initiating tab closes before announcing completion.
                recoveryTimer = setTimeout(() => reloadContext(), 15000);
            }
            if (event.data.type === 'changed') {
                reloadContext();
            }
            if (event.data.type === 'failed') {
                clearTimeout(recoveryTimer);
                setAccountTransition(false);
            }
        };
        return () => {
            clearTimeout(recoveryTimer);
            listener.close();
        };
    }, []);
}

export function useAccountSessionManager() {
    const run = useCallback(async (operation: () => Promise<void>, path: string) => {
        if (useMultiAccountStore.getState().switchingAccount) {
            return;
        }
        setAccountTransition(true);
        try {
            await withAccountSessionLock(async () => {
                broadcast('changing');
                await operation();
                useAuthStore.getState().setAccessToken(null);
                broadcast('changed');
                // A fresh document clears every resource store, pending callback, query and socket together.
                // Keeping a hand-maintained list of stores here left account data behind as the app grew.
                reloadContext(path);
            });
        } catch (error) {
            captureException(error, { tags: { operation: 'account-session.change' } });
            broadcast('failed');
            setAccountTransition(false);
            useMultiAccountStore.setState({ error: accountActionError(error) });
        }
    }, []);
    return {
        switchAccount: (accountId: string, workspaceId?: string) =>
            run(() => authSessionService.switchAccount(accountId, workspaceId), '/'),
        addAccount: () => run(authSessionService.addAccount, '/login?addAccount=true'),
        logoutAccounts: (accountIds?: string[]) => {
            const activeAccount = useMultiAccountStore.getState().context?.accountId;
            const keepActiveAccount = activeAccount && accountIds && !accountIds.includes(activeAccount);
            const destination = keepActiveAccount
                ? `${window.location.pathname}${window.location.search}${window.location.hash}`
                : '/login';
            return run(() => authSessionService.logoutAccounts(accountIds), destination);
        },
        retry: () => loadAccountSessions(),
    };
}
